import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Select from '@radix-ui/react-select';
import { parseDateOnly, formatDateForInput, birthDateDisplayValue, toCanonicalBirthDate } from '@/utils/dateFormatting';
import type { DatePrecision } from '@/utils/dateFormatting';
import DatePartsInput from './DatePartsInput';
import { DatePrecisionType } from 'we-tree-api-client/v2';

import './date-field.css';

const DATE_PRECISION_VALUES: DatePrecision[] = Object.values(DatePrecisionType);

export interface DateFieldWithPrecisionProps {
  id: string;
  label: string;
  precisionLabel?: string;
  value: string;
  precision: DatePrecision;
  onValueChange: (value: string) => void;
  onPrecisionChange: (precision: DatePrecision) => void;
  onErrorClear?: () => void;
  disabled?: boolean;
  error?: string;
  hint?: string;
  /**
   * When true, clearing the input (empty string) calls onValueChange('').
   * When false, empty input is ignored; use the Clear button to clear (birth date behavior).
   */
  allowClearViaInput?: boolean;
  /** Optional class name for the wrapper (e.g. for grid layout in Add Pet form) */
  wrapperClassName?: string;
  /**
   * Layout: 'stacked' shows precision and date as separate blocks; 'inline' puts precision select
   * and date input on the same row (useful for compact forms like Add Pet).
   */
  layout?: 'stacked' | 'inline';
}

const PRECISION_LABEL_KEYS: Record<DatePrecision, 'year' | 'month' | 'fullDate'> = {
  year: 'year',
  month: 'month',
  day: 'fullDate'
};

export const DateFieldWithPrecision: React.FC<DateFieldWithPrecisionProps> = ({
  id,
  label,
  precisionLabel,
  value,
  precision,
  onValueChange,
  onPrecisionChange,
  onErrorClear,
  disabled = false,
  error,
  hint,
  allowClearViaInput = false,
  wrapperClassName,
  layout = 'stacked'
}) => {
  const { t } = useTranslation('forms');
  const resolvedPrecisionLabel = precisionLabel ?? t('date.precision');

  const handlePrecisionChange = (nextPrecision: DatePrecision) => {
    const date = value?.trim() || '';
    let nextDate = date;
    if (date) {
      const p = parseDateOnly(date);
      if (p) {
        const m = String(p.month).padStart(2, '0');
        if (nextPrecision === 'year') nextDate = `${p.year}-01-01`;
        else if (nextPrecision === 'month') nextDate = `${p.year}-${m}-01`;
        else nextDate = formatDateForInput(date);
      }
    }
    onPrecisionChange(nextPrecision);
    if (nextDate !== date) onValueChange(nextDate);
  };

  const handleDateChange = (rawValue: string) => {
    const canonical = toCanonicalBirthDate(rawValue, precision);
    if (rawValue.trim() === '') {
      if (allowClearViaInput) {
        onValueChange('');
        onErrorClear?.();
      }
      return;
    }
    if (canonical === '') return;
    onValueChange(canonical);
    onErrorClear?.();
  };

  const handleClear = () => {
    onValueChange('');
    onErrorClear?.();
  };

  const displayValue = birthDateDisplayValue(value ?? '', precision) || value || '';

  const precisionSelect = (
    <Select.Root value={precision} onValueChange={(v) => handlePrecisionChange(v as DatePrecision)} disabled={disabled}>
      <Select.Trigger
        id={`${id}-precision`}
        className={`form-select-trigger date-field__precision-trigger ${layout === 'inline' ? 'form-select-trigger--compact' : ''}`}
        aria-label={t('date.precisionAria', { label })}
        aria-describedby={error ? undefined : `${id}-precision-desc`}
      >
        <Select.Value />
      </Select.Trigger>
      <Select.Portal>
        <Select.Content className="form-select-content" position="popper" sideOffset={4}>
          {DATE_PRECISION_VALUES.map((p) => (
            <Select.Item key={p} value={p} className="form-select-item">
              <Select.ItemText>{t(`date.${PRECISION_LABEL_KEYS[p]}`)}</Select.ItemText>
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );

  const dateInputRow = (
    <div className="date-field__date-with-clear">
      {layout === 'inline' ? precisionSelect : null}
      <DatePartsInput
        id={id}
        value={displayValue}
        onChange={handleDateChange}
        precision={precision}
        disabled={disabled}
        className={error ? 'date-field__input--error' : ''}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      <button
        type="button"
        className="date-field__clear-button"
        onClick={handleClear}
        disabled={disabled || !value?.trim()}
        aria-label={t('date.clearAria', { label })}
      >
        {t('date.clear')}
      </button>
    </div>
  );

  const content =
    layout === 'inline' ? (
      <div className="date-field">
        <label htmlFor={id} className="date-field__label">
          {label}
        </label>
        {dateInputRow}
        {error && (
          <span id={`${id}-error`} className="date-field__error-message">
            {error}
          </span>
        )}
        {hint && !error && <span className="date-field__hint">{hint}</span>}
      </div>
    ) : (
      <>
        <div className="date-field">
          <label htmlFor={`${id}-precision`} className="date-field__label">
            {resolvedPrecisionLabel}
          </label>
          {precisionSelect}
          <span id={`${id}-precision-desc`} className="date-field__hint">
            {precision === 'year'
              ? t('date.hintYear')
              : precision === 'month'
                ? t('date.hintMonth')
                : t('date.hintFull')}
          </span>
        </div>
        <div className="date-field">
          <label htmlFor={id} className="date-field__label">
            {label}
          </label>
          {dateInputRow}
          {error && (
            <span id={`${id}-error`} className="date-field__error-message">
              {error}
            </span>
          )}
          {hint && !error && <span className="date-field__hint">{hint}</span>}
        </div>
      </>
    );

  if (wrapperClassName) {
    return <div className={wrapperClassName}>{content}</div>;
  }
  return content;
};

export default DateFieldWithPrecision;
